import React from 'react';
import { FormGroup } from 'react-bootstrap';
import {
  Control,
  FieldErrors,
  UseFormGetValues,
  UseFormRegister,
  UseFormSetValue,
} from 'react-hook-form';
import { useFreeUser } from 'v2/hooks/useFreeUser';
import MultiSelectFilter from 'v2/Filters/MultiSelectFilter/MultiSelectFilter';
import TextDropDownPairFilter from 'v2/Filters/TextDropDownPairFilter/TextDropDownPairFilter';
import TextDropDownPairFilterFreeUser from 'v2/Filters/TextDropDownPairFilter/TextDropDownPairFilterFreeUser';
import Checkbox from 'v2/form/Checkbox/Checkbox';
import Select from 'v2/form/Select/Select';
import {
  FilterEntityOption,
  SearchFilterEntity,
  SEARCH_FILTER_KEYS,
} from 'v2/models';
import { CreateSearchFormData } from '../CreateSearchSchema';
import styles from './filtersStep.module.scss';
import ExtendedRequest from './ExtendedRequest/ExtendedRequest';

interface ReturnCorrectNumericFilterProps {
  isFreeUser: boolean;
  filter: SearchFilterEntity;
  control: Control<CreateSearchFormData>;
  error: string;
}

interface FiltersStepProps {
  filters: SearchFilterEntity[];
  register: UseFormRegister<CreateSearchFormData>;
  errors: FieldErrors<CreateSearchFormData>;
  control: Control<CreateSearchFormData>;
  setValue: UseFormSetValue<CreateSearchFormData>;
  getValues: UseFormGetValues<CreateSearchFormData>;
}

const ReturnCorrectNumericFilter = ({
  isFreeUser,
  filter,
  control,
  error,
}: ReturnCorrectNumericFilterProps) => {
  const TEXT_DROPDOWN_PAIR_FILTERS_PROPS = {
    label: filter.name,
    name: `filters.${filter.key}`,
    control,
    error,
    defaultValue: { unit: '', value: '' },
    options: [
      { label: 'Select one option', value: '' },
      ...filter.options.map((item) => ({
        label: item.name,
        value: item.name,
      })),
    ],
  };

  if (isFreeUser && filter.key === SEARCH_FILTER_KEYS.price) {
    return (
      <FormGroup data-cy="numeric-filter">
        <TextDropDownPairFilterFreeUser {...TEXT_DROPDOWN_PAIR_FILTERS_PROPS} />
      </FormGroup>
    );
  }

  return (
    <FormGroup data-cy="numeric-filter">
      <TextDropDownPairFilter {...TEXT_DROPDOWN_PAIR_FILTERS_PROPS} />
    </FormGroup>
  );
};

const FiltersStep = ({
  filters,
  control,
  register,
  errors,
  setValue,
  getValues,
}: FiltersStepProps) => {
  const [isFreeUser] = useFreeUser();

  const renderFilter = (filter: SearchFilterEntity) => {
    const error = getError(errors, filter);
    switch (filter.type) {
      case 'category':
        if (
          filter.key === SEARCH_FILTER_KEYS.exclude_providers ||
          filter.key === SEARCH_FILTER_KEYS.exclude_providers_drm
        ) {
          return <></>;
        } else if (filter.key === SEARCH_FILTER_KEYS.granularity) {
          return (
            <FormGroup data-cy="granular-category-filter">
              <Select
                label={filter.name}
                register={register(
                  `filters.${filter.key}` as keyof CreateSearchFormData
                )}
                error={error}
                options={[
                  { label: 'Select one option', value: '' },
                  ...filter.options.map((item) => ({
                    label: item.name,
                    value: item.name,
                  })),
                ]}
              />
            </FormGroup>
          );
        }

        return (
          <FormGroup data-cy="category-filter">
            <MultiSelectFilter
              label={filter.name}
              options={filter.options}
              name={`filters.${filter.key}`}
              error={error}
              setValue={setValue}
              selectedValues={
                (getValues(
                  `filters.${filter.key}` as unknown as keyof CreateSearchFormData
                ) as unknown as FilterEntityOption[]) || []
              }
              control={control}
            />
          </FormGroup>
        );

      case 'numeric':
        return (
          <ReturnCorrectNumericFilter
            isFreeUser={isFreeUser}
            filter={filter}
            control={control}
            error={error}
          />
        );

      case 'timediff':
        return (
          <FormGroup data-cy="timediff-filter">
            <TextDropDownPairFilter
              label={filter.name}
              name={`filters.${filter.key}`}
              control={control}
              error={error}
              defaultValue={{ unit: '', value: '' }}
              options={[
                { label: 'Select one option', value: '' },
                ...filter.options.map((item) => ({
                  label: item.name,
                  value: item.name,
                })),
              ]}
            />
          </FormGroup>
        );

      case 'boolean':
        return (
          <FormGroup data-cy="boolean-filter" className={styles.fullWidth}>
            <Checkbox
              label=""
              register={register(
                `filters.${filter.key}` as unknown as keyof CreateSearchFormData
              )}
              text={filter.name}
              error={error}
            />
          </FormGroup>
        );
      default:
        return null;
    }
  };

  return (
    <div className="px-3">
      <p className="text-light8">
        All data Requirements are optional, but are treated as necessities by
        data providers. Unless it is a hard and fast requirement it&apos;s
        always better to ask a Question rather than stipulate a Requirement.
      </p>
      <div className={styles.padded}>
        <div
          className={styles.filterListContainer}
          data-cy="request-filters-container"
        >
          {filters.map((item) => (
            <React.Fragment key={item.id}>{renderFilter(item)}</React.Fragment>
          ))}
        </div>
        <ExtendedRequest
          control={control}
          setValue={setValue}
          getValues={getValues}
          register={register}
        />
      </div>
    </div>
  );
};

export default FiltersStep;

function getError(
  errors: FieldErrors<CreateSearchFormData>,
  filter: SearchFilterEntity
): string {
  const error = errors?.filters?.[`${filter.key}`];

  if (typeof error === 'string') {
    return error;
  }
  let erroMsg = '';
  if (typeof error === 'object') {
    const filterErr =
      error as unknown as FieldErrors<CreateSearchFormData>['filters'];
    if (filterErr) {
      if ('value' in filterErr) {
        erroMsg = 'Please enter a valid value.';
      }
      if ('unit' in filterErr) {
        erroMsg += ' Please enter a valid Unit.';
      }
    }
  }
  return erroMsg.trim();
}
